// ── Commit helpers (L4 write-only, skip-if-unchanged) ────────────────────
// Any DOM write that runs on the rAF loop should go through these helpers.
// They cache the last committed value per (element, key) and short-circuit
// no-op writes, so a frame where nothing changed produces zero DOM mutations.

const commitCache = new WeakMap<Element, Map<string, string>>();

function cacheFor(el: Element): Map<string, string> {
  let map = commitCache.get(el);
  if (!map) {
    map = new Map();
    commitCache.set(el, map);
  }
  return map;
}

export function setStyleIfChanged(
  el: HTMLElement | SVGElement,
  key: string,
  value: string,
): boolean {
  const cache = cacheFor(el);
  const k = `s:${key}`;
  if (cache.get(k) === value) return false;
  cache.set(k, value);
  (el.style as unknown as Record<string, string>)[key] = value;
  return true;
}

export function setAttrIfChanged(el: Element, name: string, value: string): boolean {
  const cache = cacheFor(el);
  const k = `a:${name}`;
  if (cache.get(k) === value) return false;
  cache.set(k, value);
  el.setAttribute(name, value);
  return true;
}

export function setClassIfChanged(el: Element, name: string, on: boolean): boolean {
  const cache = cacheFor(el);
  const k = `c:${name}`;
  const next = on ? '1' : '0';
  if (cache.get(k) === next) return false;
  cache.set(k, next);
  el.classList.toggle(name, on);
  return true;
}

/** Forget the cached last-committed values for an element. Call when the
 *  element has been externally reset (e.g. `style=''` assignment) and the
 *  commit layer's cache would otherwise skip a needed write. */
export function invalidateCommit(el: Element): void {
  commitCache.delete(el);
}
